// src/components/RoomsPage.js
import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";
import RoomFormModal from "./RoomFormModal";
import AdminTableWrapper from "./AdminTableWrapper";

export default function RoomsPage() {
  const [rooms, setRooms] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);

  const [role, setRole] = useState("staff");
  const [roleLoading, setRoleLoading] = useState(true);

  const roomsCollection = collection(db, "rooms");

  // Load role
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setRole("staff");
        setRoleLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const data = snap.exists() ? snap.data() : null;
        setRole(data?.role || "staff");
      } catch (err) {
        console.error("Failed to load user role:", err);
        setRole("staff");
      } finally {
        setRoleLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const canEdit = role === "superadmin" || role === "admin";

  const loadRooms = async () => {
    try {
      const snapshot = await getDocs(roomsCollection, { source: "server" });
      setRooms(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Failed loading rooms:", error);
    }
  };

  useEffect(() => {
    loadRooms();
  }, []);

  const openAddModal = () => {
    if (!canEdit) return;
    setEditingRoom(null);
    setModalOpen(true);
  };

  const openEditModal = (room) => {
    if (!canEdit) return;
    setEditingRoom(room);
    setModalOpen(true);
  };

  const handleSubmit = async (data) => {
    if (!canEdit) return;

    if (editingRoom) {
      await updateDoc(doc(db, "rooms", editingRoom.id), data);
    } else {
      await addDoc(roomsCollection, data);
    }

    setModalOpen(false);
    loadRooms();
  };

  const deleteRoom = async (id) => {
    if (!canEdit) return;
    if (!window.confirm("Are you sure you want to delete this room?")) return;

    try {
      await deleteDoc(doc(db, "rooms", id));
      setRooms((prevRooms) => prevRooms.filter((room) => room.id !== id));
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  return (
    <div className="w-full">
      {/* Page Title */}
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Rooms Management</h1>
      </div>

      {/* Shared admin table wrapper (handles card look + mobile scroll) */}

    {/* Table */}
    <AdminTableWrapper>
      {/* Top Buttons */}
      <div className="flex justify-between items-center mb-4">
        <div />
        {canEdit ? (
          <button
            onClick={openAddModal}
            className="px-4 py-2 rounded-full bg-[#b4642d] text-white hover:bg-[#9a5525] transition"
          >
            + Add Room
          </button>
        ) : (
          <span className="text-xs text-gray-500 italic">
            Read-only access – contact admin for changes
          </span>
        )}
      </div>

      {roleLoading && (
        <p className="text-xs text-gray-400 mb-4">
          Checking permissions…
        </p>
      )}

      {/* Mobile-safe table wrapper for iOS Safari */}
      <div className="block w-full overflow-x-scroll">
        <table className="min-w-[450px] w-full text-left border-separate border-spacing-0">
          <thead className="bg-orange-50/60 text-gray-700">
            <tr>
              <th className="text-left p-3">Room</th>
              <th className="text-left p-3">Price (₦)</th>
              <th className="text-left p-3">Status</th>
              <th className="text-center p-3">Actions</th>
            </tr>
          </thead>

          <tbody>
            {rooms.map((r) => (
              <tr
                key={r.id}
                className="border-b border-amber-100 bg-white hover:bg-orange-50/30 transition"
              >
                <td className="p-3 font-medium whitespace-nowrap">{r.roomNo}</td>

                <td className="p-3 font-semibold text-[#b4642d] whitespace-nowrap">
                  {r.price ? `₦${Number(r.price).toLocaleString()}` : "--"}
                </td>

                <td className="p-3 whitespace-nowrap">
                  {r.status === "Occupied" && (
                    <span className="px-2 py-1 text-xs rounded bg-red-600 text-white">
                      Occupied
                    </span>
                  )}

                  {r.status === "Available" && (
                    <span className="px-2 py-1 text-xs rounded bg-green-600 text-white">
                      Available
                    </span>
                  )}

                  {r.status === "Maintenance" && (
                    <span className="px-2 py-1 text-xs rounded bg-yellow-600 text-white">
                      Maintenance
                    </span>
                  )}
                </td>

                <td className="p-3 text-center space-x-3 whitespace-nowrap">
                  {canEdit ? (
                    <>
                      <button
                        className="text-blue-600 hover:underline"
                        onClick={() => openEditModal(r)}
                      >
                        Edit
                      </button>

                      <button
                        className="text-red-600 hover:underline"
                        onClick={() => deleteRoom(r.id)}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400 italic">
                      View only
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminTableWrapper>

      <RoomFormModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        room={editingRoom}
      />
    </div>
  );
}
